import axios from "axios";
import { proxyUrl } from "../config";
import { setupInterceptorsTo } from "./Interceptors";
import { AccountsAutorized, AccountsType, AccountsUsed, SubscriptionType } from "./SubscriptionService";
import { UserIdentityType } from "./UserIdentitiesService";
import { UsersSubscriptionType } from "./UsersSubscriptionService";
setupInterceptorsTo(axios);

interface IUser
{
    id?: number;
    druId?: string;
    userIdentities?: UserIdentityType[];
    mainUserIdentity: UserIdentityType;
    usersSubscriptions?: UsersSubscriptionType[];
    finametrixId?: string;
    status?: any;
    email?: string;
    afilliate?: IAfilliate;
    afilliateId?: string;
    createdAt?: string;
    updatedAt?: string;
}
export type UserType = IUser;

interface IAfilliate
{
    id: string;
    name: string;
    email: string;
    trust: boolean;
    notificattionUrlOk?: string;
    notificattionUrlKo?: string;
    createdAt?: string;
    updatedAt?: string;
}

export type Afilliate = IAfilliate;

interface PortfolioItem
{
    bmePortfolio: any;
    finametrixId: string;
    id: number;
    managedPortfolioFinametrixId: number;
    sharedPortfolio: any;
}
interface IUserActivePlan
{
    cancelled: boolean;
    fnmContractId: number;
    finametrixContract: string;
    outdated: boolean;
    outdatedContract: boolean;
    planId: number;
    planName: string;
    portfolio: PortfolioItem;
    status: number;
    type: number;
    userId: number;
    userSubscriptionId: number;
    allUserIdentitiesHaveSuitabilityTest: boolean;
}

export type UserActive = IUserActivePlan;

// Timeout de la subida de cuentas bancarias con certificado de titularidad.
// El balanceador de Azure corta las peticiones HTTP a los 230 s, así que un valor mayor no aporta;
// sin timeout, una petición colgada deja el modal "Procesando la operación" abierto indefinidamente.
const PostBankAccountTimeoutMs = 240000;

class UserService
{
    public getUser = async (userId: number): Promise<UserType> =>
    {
        let url: string = `${proxyUrl}/users/${userId}`;
        const req = await axios.get<UserType>(url);
        return req.data;
    };
    public getUserByDruid = async (userDruId: string): Promise<UserType> =>
    {
        let url: string = `${proxyUrl}/users/getByDruId/${userDruId}`;
        const req = await axios.get<UserType>(url);
        return req.data;
    };
    public getUserLastActivePlan = async (userId: any): Promise<UserActive> =>
    {
        let url: string = `${proxyUrl}/getActivePlan/${userId}`;
        const req = await axios.get<UserActive>(url);
        return req.data;
    };
    public createUser = async (user: UserType): Promise<UserType> =>
    {
        let url: string = `${proxyUrl}/users/${user.id}`;
        const req = await axios.post(url, user);
        return req.data;
    };
    public updateUser = async (user: UserType): Promise<UserType> =>
    {
        let url: string = `${proxyUrl}/users/${user.id}`;
        const req = await axios.patch(url, user);
        return req.data;
    };
    public getSuscriptions = async (userId: number): Promise<SubscriptionType[]> =>
    {
        let url: string = `${proxyUrl}/users/${userId}/subscriptions`;
        const req = await axios.get(url);
        return req.data;
    };
    public getBankAccountsAutorized = async (userId: number): Promise<AccountsAutorized[]> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts`;
        const req = await axios.get(url);
        return req.data;
    };
    public getBankAccountsAuthorizedBySavingPlanId = async (userId: number, savingPlanId: number): Promise<AccountsAutorized[]> =>
    {
        let url: string = `${proxyUrl}/UserAccountAuthorizedBySavingPlanId/${savingPlanId}/${userId}`;
        const req = await axios.get(url);
        return req.data;
    };
    public getBankAccountsUsed = async (userId: number): Promise<AccountsUsed[]> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts/used`;
        const req = await axios.get(url);
        return req.data;
    };
    public getBankAccountsUsedWithParam = async (userId: number, param: string): Promise<AccountsUsed[]> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts/used?jwt=${param}`;
        const req = await axios.get(url);
        return req.data;
    };
    public postBankAccount = async (toSend: FormData, userId: number): Promise<AccountsUsed[]> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts`;
        const req = await axios.post<any>(url, toSend, { timeout: PostBankAccountTimeoutMs });
        return req.data;
    };
    public selectBankAccount = async (toSend: FormData, userId: number): Promise<AccountsUsed[]> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts`;
        const req = await axios.patch<any>(url, toSend);
        return req.data;
    };
    public deleteBankAccount = async (bankAccountId: number, userId: number): Promise<AccountsType[]> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts?idUserBankAccount=${encodeURIComponent(bankAccountId)}`;
        const req = await axios.delete(url);
        return req.data;
    };
    public dowloadBase64Pdf = async (bankAccountId: number, userId: number): Promise<any> =>
    {
        let url: string = `${proxyUrl}/user/${userId}/accounts/authorized/document?idUserBankAccount=${encodeURIComponent(bankAccountId)}`;
        const req = await axios.get<any>(url, { responseType: "blob" });
        const reader = new FileReader();
        return new Promise((resolve) =>
        {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(req.data);
        });
    };
    public selectActivePlan = async (userId: number, planId: number): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/updateActivePlan/${userId}/${planId}`;
        const req = await axios.patch(url);
        return req.data;
    };
    public selectActivePlanByName = async (userId: number, planName: string): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/updateActivePlanByName/${userId}/${planName}`;
        const req = await axios.patch(url);
        return req.data;
    };
    public setConfigurationMap = async (userId: number, value: boolean): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/setConfigurationMap/${userId}/${value}`;
        const req = await axios.patch(url);
        return req.data;
    };
    public setUserStatus = async (userId: number, statusValue: string): Promise<boolean> =>
    {
        let url: string = `${proxyUrl}/setUserStatus/${userId}/${statusValue}`;
        const req = await axios.patch(url);
        return req.data;
    };
    public validateUserEmail = async (): Promise<boolean> =>
    {
        const url = `${proxyUrl}/userValidateEmail`;
        try
        {
            const res = await axios.get(url);
            return res.data;
        }
        catch(error: any)
        {
            throw error;
        }
    };
}

const users = new UserService();
export default users;